import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { toast } from "@grandvisionhq/common";
import { useMutation } from "@grandvisionhq/graphql";
import { useCartStorage } from "@grandvisionhq/lib-utils-cart";
import { useDispatchTrackEvent, useDispatchTrackErrorEvent } from "@grandvisionhq/svc-tealium/events";
import { isNil } from "@grandvisionhq/utils";
import { mapRemovedProductToTealiumProduct, mapLineItemProductToTealiumProduct } from "../../mappers/analytics/tealium/mappers/analytics/tealium/line-item-to-tealium-product.js";
import { mapLineItemToDataLayerEvent } from "../../mappers/analytics/data-layer/mappers/analytics/data-layer/line-item-to-data-layer-event.js";
import { getValidationErrorCodes } from "../../utils/utils/get-validation-error-codes.js";
import { UPDATE_CART_LINE_ITEMS } from "../../operations/mutations/operations/mutations/update-cart-line-items.js";
import { GET_CART } from "../../operations/queries/operations/queries/get-cart.js";
const useUpdateLineItemQuantity = (lineItem) => {
  const [{ cartId }, { setCartCount }] = useCartStorage();
  const [quantityDiff, setQuantityDiff] = React.useState(0);
  const dispatchTrackEvent = useDispatchTrackEvent();
  const dispatchTrackErrorEvent = useDispatchTrackErrorEvent();
  const handleValidationErrors = (validationErrors) => {
    validationErrors.forEach((validationError) => toast.failed(`cart.${validationError}`));
  };
  const handleAddToCartTealium = (lineItem2) => {
    const Products = mapLineItemProductToTealiumProduct([lineItem2]);
    dispatchTrackEvent(
      () => Promise.resolve({
        id: "Event",
        Events_CartAdd: "1",
        Products
      })
    );
  };
  const handleRemoveFromCartTealium = React.useCallback(() => {
    dispatchTrackEvent(
      () => Promise.resolve({
        id: "Event",
        Events_CartRemove: "1",
        Products: mapRemovedProductToTealiumProduct(lineItem)
      })
    );
  }, [dispatchTrackEvent, lineItem]);
  const [updateCartItems, updateQuantityMutation] = useMutation(UPDATE_CART_LINE_ITEMS, {
    onCompleted: () => {
      if (quantityDiff === 0) {
        return;
      }
      const id = cartId || "";
      const eventType = quantityDiff > 0 ? DataLayer.DataLayerTypes.EventType.add : DataLayer.DataLayerTypes.EventType.remove;
      const dataLayerEvents = mapLineItemToDataLayerEvent(
        eventType,
        [{ ...lineItem, quantity: Math.abs(quantityDiff) }],
        id
      );
      dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
      quantityDiff > 0 ? handleAddToCartTealium({ ...lineItem, quantity: Math.abs(quantityDiff) }) : handleRemoveFromCartTealium();
    },
    refetchQueries: ["GetCart"]
  });
  const updateItemQuantity = React.useCallback(
    (updatedQuantity) => {
      if (isNil(cartId)) {
        console.error("No cart ID available");
        dispatchTrackErrorEvent(
          () => Promise.resolve({
            Error_Source: "Server",
            Error_Code: "Update cart line item quantity error",
            Error_Details: "No cart ID available"
          })
        );
        return;
      }
      setQuantityDiff(updatedQuantity - lineItem.quantity);
      updateCartItems({
        variables: {
          cartInput: {
            cartId,
            lineItems: [
              {
                id: lineItem.id,
                quantity: updatedQuantity
              }
            ]
          }
        },
        update(cache, { data }) {
          if (data && data.updateCartLineItems.success === true) {
            const newCart = data.updateCartLineItems.cart;
            cache.writeQuery({
              query: GET_CART,
              variables: { id: cartId },
              data: { cart: { cart: newCart, validationErrors: null, failures: null } }
            });
            setCartCount(newCart?.numberOfItems || 0);
          }
          if (data && !data.updateCartLineItems.success && data.updateCartLineItems.validationErrors) {
            const validationErrors = getValidationErrorCodes(
              data.updateCartLineItems.validationErrors
            );
            handleValidationErrors(validationErrors);
          }
        }
      });
    },
    [cartId, dispatchTrackErrorEvent, lineItem.id, lineItem.quantity, setCartCount, updateCartItems]
  );
  const { called, loading, error } = updateQuantityMutation;
  return [updateItemQuantity, { called, loading, error }];
};
export {
  useUpdateLineItemQuantity
};
//# sourceMappingURL=use-update-line-item-quantity.js.map
